(() => {
  document.documentElement.classList.add('irw-no-marketing-popups');

  if (!window.__irwMarketingPopupGuard) {
    const suppressMarketingPopup = () => {
      document
        .querySelectorAll(
          '[data-testid="POPUP"], [data-testid="POPUP_FORM"], [class*="kl-private-reset-css"]'
        )
        .forEach((element) => {
          const popup = element.matches('[data-testid="POPUP"], [data-testid="POPUP_FORM"]')
            ? element.closest('[role="dialog"]') || element
            : element.closest('[role="dialog"]');
          if (!popup) return;
          popup.style.setProperty('display', 'none', 'important');
          popup.setAttribute('aria-hidden', 'true');
        });

      document.documentElement.style.removeProperty('overflow');
      document.body?.style.removeProperty('overflow');
    };

    const popupObserver = new MutationObserver(suppressMarketingPopup);
    popupObserver.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
    window.__irwMarketingPopupGuard = popupObserver;
    suppressMarketingPopup();
  }

  const init = () => {
    document.querySelectorAll('[data-returning-reward]').forEach((root) => {
      const form = root.querySelector('[data-reward-form]');
      const success = root.querySelector('[data-reward-success]');
      const platformInput = root.querySelector('[data-platform-input]');
      const orderInput = root.querySelector('[data-order-input]');
      const orderHint = root.querySelector('[data-order-hint]');
      const error = root.querySelector('[data-reward-error]');
      const submit = form?.querySelector('button[type="submit"]');
      const code = root.querySelector('[data-discount-code]');
      const message = root.querySelector('[data-success-message]');
      const signedCode = root.querySelector('[data-signed-discount-code]');
      const copyButton = root.querySelector('[data-copy-reward-code]');
      const copyStatus = root.querySelector('[data-copy-status]');
      const issuedCopyButton = root.querySelector('[data-copy-issued-code]');
      const issuedCopyStatus = root.querySelector('[data-issued-copy-status]');
      const successExpiry = root.querySelector('[data-success-expiry]');
      const signedReward = root.querySelector('[data-signed-reward]');
      const signedRewardError = root.querySelector('[data-signed-reward-error]');
      const retrySignedReward = root.querySelector('[data-retry-signed-reward]');
      const marketplaceHelp = root.querySelector('[data-marketplace-help]');
      const shopifyFallbackMessage = root.querySelector('[data-shopify-fallback-message]');

      const previewPersonalCode = () => {
        const storageKey = 'inkelligent-preview-personal-reward';
        let value = window.sessionStorage.getItem(storageKey);
        if (value) return value;

        const bytes = new Uint8Array(5);
        window.crypto.getRandomValues(bytes);
        value = `INKVIP-${Array.from(bytes, (byte) =>
          byte.toString(16).padStart(2, '0')
        ).join('').toUpperCase()}`;
        window.sessionStorage.setItem(storageKey, value);
        return value;
      };

      const showSignedCode = (value) => {
        if (signedCode) signedCode.textContent = value;
        if (copyButton) copyButton.disabled = false;
        if (signedRewardError) signedRewardError.hidden = true;
        if (retrySignedReward) retrySignedReward.hidden = true;
      };

      const showMarketplaceFallback = () => {
        if (!form) return;
        if (signedReward) signedReward.hidden = true;
        form.hidden = false;
        if (marketplaceHelp) marketplaceHelp.hidden = false;
        if (shopifyFallbackMessage) {
          shopifyFallbackMessage.textContent =
            'We could not verify an eligible website order. You can still verify an Amazon or Etsy order below.';
        }
        orderInput?.focus();
      };

      const loadSignedReward = async () => {
        if (!signedCode) return;
        if (root.dataset.previewMode === 'true') {
          showSignedCode(previewPersonalCode());
          return;
        }

        const endpoint = (root.dataset.shopifyClaimPath || '').trim();
        if (!endpoint) {
          showMarketplaceFallback();
          return;
        }

        if (copyButton) copyButton.disabled = true;
        signedCode.textContent = 'CHECKING…';
        try {
          const response = await fetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'same-origin',
            body: '{}',
          });
          const result = await response.json().catch(() => ({}));
          if (!response.ok || !result.eligible || !result.discount_code) {
            throw new Error(result.message || 'We could not prepare your personal reward.');
          }
          showSignedCode(result.discount_code);
        } catch {
          showMarketplaceFallback();
        }
      };

      retrySignedReward?.addEventListener('click', loadSignedReward);
      loadSignedReward();

      const platformButtons = Array.from(root.querySelectorAll('[data-platform]'));

      const selectPlatform = (button) => {
        const platform = button.dataset.platform || '';

        platformButtons.forEach((item) => {
          const active = item === button;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-pressed', String(active));
        });

        if (platformInput) platformInput.value = platform;
        if (orderInput) orderInput.placeholder = button.dataset.placeholder || '';
        if (orderHint) orderHint.textContent = button.dataset.hint || '';
      };

      platformButtons.forEach((button) => {
        button.addEventListener('click', () => selectPlatform(button));
      });

      const initiallyActive =
        platformButtons.find((button) => button.classList.contains('is-active')) ||
        platformButtons[0];
      if (initiallyActive) selectPlatform(initiallyActive);

      const copyValue = async (value) => {
        if (!value) return;

        try {
          await navigator.clipboard.writeText(value);
        } catch {
          const temporaryInput = document.createElement('textarea');
          temporaryInput.value = value;
          temporaryInput.setAttribute('readonly', '');
          temporaryInput.style.position = 'fixed';
          temporaryInput.style.opacity = '0';
          document.body.appendChild(temporaryInput);
          temporaryInput.select();
          document.execCommand('copy');
          temporaryInput.remove();
        }
      };

      copyButton?.addEventListener('click', async () => {
        const value = signedCode?.textContent?.trim();
        if (!value) return;
        await copyValue(value);

        copyButton.textContent = 'Copied';
        if (copyStatus) copyStatus.textContent = 'Your member offer was copied.';
        window.setTimeout(() => {
          copyButton.textContent = 'Copy member offer';
          if (copyStatus) copyStatus.textContent = '';
        }, 2400);
      });

      issuedCopyButton?.addEventListener('click', async () => {
        const value = code?.textContent?.trim();
        if (!value) return;
        await copyValue(value);

        issuedCopyButton.textContent = 'Copied';
        if (issuedCopyStatus) {
          issuedCopyStatus.textContent = 'Member offer copied to your clipboard.';
        }
        window.setTimeout(() => {
          issuedCopyButton.textContent = 'Copy member offer';
          if (issuedCopyStatus) issuedCopyStatus.textContent = '';
        }, 2400);
      });

      form?.addEventListener('submit', async (event) => {
        event.preventDefault();
        error.hidden = true;

        if (!form.reportValidity()) return;

        if (root.dataset.previewMode === 'true') {
          if (code) code.textContent = previewPersonalCode();
          form.hidden = true;
          success.hidden = false;
          success.focus();
          return;
        }

        const endpoint = (root.dataset.apiPath || '').trim();
        if (!endpoint) {
          error.textContent = 'Order verification is not connected yet.';
          error.hidden = false;
          error.focus();
          return;
        }

        submit.disabled = true;
        const originalLabel = submit.firstElementChild?.textContent;
        if (submit.firstElementChild) submit.firstElementChild.textContent = 'Checking order\u2026';
        /* Legacy malformed loading label retained inside this comment.
        if (submit.firstElementChild) submit.firstElementChild.textContent = 'Checking order…';

        */
        try {
          const payload = Object.fromEntries(new FormData(form).entries());
          const response = await fetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify(payload),
          });
          const result = await response.json().catch(() => ({}));

          if (!response.ok || !result.eligible) {
            throw new Error(result.message || 'We could not verify this order.');
          }

          if (code) code.textContent = result.discount_code || 'ORDER VERIFIED';
          if (message && result.message) message.textContent = result.message;
          if (successExpiry) {
            if (result.expires_at) {
              const expiresAt = new Date(result.expires_at);
              successExpiry.textContent = Number.isNaN(expiresAt.getTime())
                ? ''
                : `Use by ${new Intl.DateTimeFormat(undefined, {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  }).format(expiresAt)}.`;
            } else {
              successExpiry.textContent = '';
            }
          }
          form.hidden = true;
          success.hidden = false;
          success.focus();
        } catch (requestError) {
          error.textContent =
            requestError instanceof Error
              ? requestError.message
              : 'We could not verify this order. Please try again.';
          error.hidden = false;
          error.focus();
        } finally {
          submit.disabled = false;
          if (submit.firstElementChild && originalLabel) {
            submit.firstElementChild.textContent = originalLabel;
          }
        }
      });

      root.querySelector('[data-reward-reset]')?.addEventListener('click', () => {
        form.reset();
        if (platformButtons[0]) selectPlatform(platformButtons[0]);
        form.hidden = false;
        success.hidden = true;
        error.hidden = true;
        if (issuedCopyStatus) issuedCopyStatus.textContent = '';
        if (successExpiry) successExpiry.textContent = '';
        orderInput?.focus();
      });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
